/**
 * VLE Consent Filters Component
 *
 * Filter panel for VLE consent list with:
 * - Date range picker (From/To)
 * - Mobile number search
 * - Consent status dropdown
 * - Apply/Reset buttons
 * - Request New Statement button
 *
 * @author Claude Code
 * @date February 2026
 * @doc Reference: Doc 32 - Phase 2 Implementation
 */

import React, { useState } from 'react';
import {
  Button,
  DatePicker,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  SearchBar,
  ChevronIcon,
} from 'equal-ds-ui';
import { Plus, Search, RotateCcw, Check } from 'lucide-react';
import type { VleConsentFiltersProps } from './types';

/**
 * Consent status options for dropdown
 */
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'REVOKED', label: 'Revoked' },
  { value: 'REJECTED', label: 'Rejected' },
];

export const VleConsentFilters: React.FC<VleConsentFiltersProps> = ({
  filters,
  onFilterChange,
  onApply,
  onReset,
  onCreateClick,
  isLoading,
}) => {
  const [isStatusHovered, setIsStatusHovered] = useState(false);

  /**
   * Handle date from change
   */
  const handleDateFromChange = (date: Date | null) => {
    onFilterChange({
      ...filters,
      dateFrom: date ? date.toISOString().split('T')[0] : '',
    });
  };

  /**
   * Handle date to change
   */
  const handleDateToChange = (date: Date | null) => {
    onFilterChange({
      ...filters,
      dateTo: date ? date.toISOString().split('T')[0] : '',
    });
  };

  /**
   * Handle mobile search change
   */
  const handleMobileChange = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    onFilterChange({
      ...filters,
      mobileSearch: numericValue,
    });
  };

  /**
   * Handle status change
   */
  const handleStatusChange = (value: string) => {
    onFilterChange({
      ...filters,
      status: value,
    });
  };

  /**
   * Handle search submit (from SearchBar)
   */
  const handleSearchSubmit = () => {
    onApply();
  };

  /**
   * Get display label for selected status
   */
  const getSelectedStatusLabel = (): string => {
    const selected = STATUS_OPTIONS.find(opt => opt.value === filters.status);
    return selected?.label || 'All Statuses';
  };

  /**
   * Create trigger styles with hover effect
   */
  const createTriggerStyles = (isHovered: boolean) => ({
    transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: isHovered ? 'var(--color-background-primary)' : 'var(--color-background-secondary)',
    color: 'var(--color-text-primary)',
    borderColor: isHovered ? 'var(--color-border-hover)' : 'var(--color-border-default)',
    fontSize: 'var(--typography-fontSize-sm)',
    fontWeight: 'var(--typography-fontWeight-medium)',
    letterSpacing: '0.05em',
    padding: 'var(--spacing-2)',
    borderRadius: 'var(--border-radius-lg)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderBottomWidth: '2px',
    boxShadow: isHovered ? 'var(--shadow-md)' : 'var(--core-shadows-sm)',
  });

  return (
    <div className="equal-ds-root">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        {/* Header with title and create button */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
          <Button
            variant="primary"
            size="md"
            onClick={onCreateClick}
            disabled={isLoading}
          >
            <Plus size={16} className="mr-2" />
            Request New Statement
          </Button>
        </div>

        {/* Filter controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Date From */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              From Date
            </label>
            <DatePicker
              selectedDate={filters.dateFrom ? new Date(filters.dateFrom) : undefined}
              onChange={handleDateFromChange}
              placeholder="Select start date"
              maxDate={new Date()}
            />
          </div>

          {/* Date To */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              To Date
            </label>
            <DatePicker
              selectedDate={filters.dateTo ? new Date(filters.dateTo) : undefined}
              onChange={handleDateToChange}
              placeholder="Select end date"
              maxDate={new Date()}
              minDate={filters.dateFrom ? new Date(filters.dateFrom) : undefined}
            />
          </div>

          {/* Mobile Search */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              Mobile Number
            </label>
            <SearchBar
              value={filters.mobileSearch}
              onChange={handleMobileChange}
              onSearch={handleSearchSubmit}
              placeholder="Search mobile..."
              className="w-full"
            />
          </div>

          {/* Status Dropdown */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              Status
            </label>
            <Dropdown>
              <DropdownTrigger
                variant="default"
                className="w-full"
                style={createTriggerStyles(isStatusHovered)}
                onMouseEnter={() => setIsStatusHovered(true)}
                onMouseLeave={() => setIsStatusHovered(false)}
              >
                <span className={`flex-1 text-left ${!filters.status ? 'text-gray-500' : ''}`}>
                  {getSelectedStatusLabel()}
                </span>
                <ChevronIcon isOpen={false} />
              </DropdownTrigger>
              <DropdownContent className="max-h-60 overflow-y-auto">
                {STATUS_OPTIONS.map((option) => (
                  <DropdownItem
                    key={option.value}
                    onClick={() => handleStatusChange(option.value)}
                  >
                    <span className="flex items-center justify-between w-full">
                      {option.label}
                      {filters.status === option.value && (
                        <Check size={16} className="text-primary-600" />
                      )}
                    </span>
                  </DropdownItem>
                ))}
              </DropdownContent>
            </Dropdown>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col justify-end">
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="md"
                onClick={onApply}
                disabled={isLoading}
                className="flex-1"
              >
                <Search size={14} className="mr-1" />
                Apply
              </Button>
              <Button
                variant="secondary"
                size="md"
                onClick={onReset}
                disabled={isLoading}
              >
                <RotateCcw size={14} className="mr-1" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VleConsentFilters;
